// Agenda partagé par l'accueil et le planning : les occurrences à venir, qui
// parmi les miens est convoqué, sa présence (attendu par défaut, D-026), et
// pour le staff les présences prévues de chaque séance de ses équipes.
import {
  invitesForEvents, matchesFor, myInvites, myResponses, responsesForEvents, teamRoster, visibleEvents,
  type ClubEvent, type MatchRow, type Team,
} from '../lib/api';
import { addDays, agenda, localDate, type Occurrence } from '../domain/schedule';
import { attendanceCounts, resolveAttendance, verdictOf, type Unavailability } from '../domain/attendance';
import type { Verdict, WeeklyRule } from '../domain/availability';
import { matchTeamLabel, modOn, teamOf, type Ctx } from './context';
import { pendingHallOcc } from '../lib/salles-api';
import { displayName } from '../domain/labels';
import { inSquad } from '../domain/squads';
import { attCtx, leadsFor, loadAvail, type AvailData, type Leads } from './availability-data';

/** Présence d'une personne à une occurrence. Pas de « n'a pas répondu » : sans réponse, on est attendu.
 *  `uncertain` : sa réponse « Incertaine » (D-116) ; `unavailable` : absente d'office (période ou règle, D-114). */
export type Presence = 'expected' | 'confirmed' | 'absent' | 'unavailable' | 'uncertain';

export interface AgendaItem {
  occ: Occurrence;
  ev: ClubEvent;
  team: Team | null;              // null : séance d'une autre équipe (renfort)
  match: MatchRow | undefined;
  /** Nom affiché de l'équipe : l'équipe engagée d'un match (SF2), sinon le groupe. */
  label: string;
  /** Les miens convoqués : leur présence, ce que dit la règle (d'office, sans échauffement, ou l'exception). */
  mine: { personId: string; presence: Presence; verdict: Verdict | null; reason: string | null }[];
  counts: { present: number; uncertain: number; absent: number; total: number } | null;   // staff de l'équipe
  /** Staff : les incertaines (et leur mot), pour « Incertaines à confirmer » (D-116). */
  uncertain: { personId: string; reason: string | null; name: string }[];
}

export interface Agenda { today: string; tomorrow: string; items: AgendaItem[]; unav: Unavailability[]; rules: WeeklyRule[]; avail: AvailData; leads: Map<string, Leads> }

export async function loadAgenda(ctx: Ctx, days = 21): Promise<Agenda> {
  const today = localDate(new Date());
  const to = addDays(today, days - 1);
  const ids = ctx.people.map(p => p.id);
  const [allEvents, responses, invites, avail, lost] = await Promise.all([
    visibleEvents(), myResponses(ids, today, to), myInvites(ids, today, to), loadAvail(),
    // D-130 : une séance dont la salle est reprise, sans choix encore : « Salle à confirmer ».
    ctx.clubs.some(c => modOn(ctx, 'salles', c.id)) ? pendingHallOcc(today, to).catch(() => []) : Promise.resolve([] as { event_id: string; day: string }[]),
  ]);
  const unsure = new Set(lost.map(x => `${x.event_id}:${x.day}`));
  // Chaque casquette ne montre que ses équipes (D-109) : en coach, ses matchs et ses séances, pas tous ceux
  // du club qu'il voit aussi comme membre du bureau ; une convocation en renfort dans une autre équipe reste.
  const teamIds = new Set(ctx.teams.map(t => t.id));
  const invited = new Set(invites.map(i => i.event_id));
  const events = allEvents.filter(e => teamIds.has(e.team_id) || invited.has(e.id));
  const byId = new Map(events.map(e => [e.id, e]));
  const occ = agenda(events, today, to);
  const matchOf = new Map((await matchesFor([...new Set(occ.filter(o => byId.get(o.eventId)!.kind === 'match').map(o => o.eventId))]))
    .map(m => [m.event_id, m]));
  // Les avances de chaque club (rendez-vous d'un match, D-114).
  const clubIds = [...new Set(events.map(e => teamOf(ctx, e.team_id)?.club_id ?? ctx.club?.id).filter((x): x is string => !!x))];
  const leads = new Map(await Promise.all(clubIds.map(async c => [c, await leadsFor(c)] as const)));
  const leadsOf = (ev: ClubEvent) => leads.get(teamOf(ctx, ev.team_id)?.club_id ?? ctx.club?.id ?? '') ?? { arrive: 60, home: 45 };

  // Staff : présences prévues de chaque séance de ses équipes.
  const staffEvents = events.filter(e => ctx.staffOf.has(e.team_id)).map(e => e.id);
  const staffTeams = [...new Set(events.filter(e => ctx.staffOf.has(e.team_id)).map(e => e.team_id))];
  const [teamResp, teamInv, rosters] = await Promise.all([
    responsesForEvents(staffEvents, today, to), invitesForEvents(staffEvents, today, to),
    Promise.all(staffTeams.map(t => teamRoster(t))),
  ]);
  const rosterOf = new Map(staffTeams.map((t, i) => [t, rosters[i]!.filter(r => r.kind === 'member')]));
  const rosterAll = new Map(staffTeams.map((t, i) => [t, rosters[i]!]));

  const items = occ.map((o): AgendaItem => {
    const ev0 = byId.get(o.eventId)!;
    const ev = unsure.has(`${o.eventId}:${o.date}`) ? { ...ev0, location: 'Salle à confirmer' } : ev0;
    const match = matchOf.get(ev.id);
    // Un match d'une équipe engagée (SF2) ne convoque que ses joueuses (D-042).
    const members = ctx.memberships.filter(m => m.team_id === ev.team_id && inSquad(m.squad_ids, match?.squad_id)).map(m => m.person_id);
    const inv = invites.filter(i => i.event_id === o.eventId && i.occurrence_date === o.date).map(i => i.person_id);
    const convoked = [...new Set([...members, ...inv])].filter(pid => ids.includes(pid));
    const actx = attCtx(avail, ev, o, match, leadsOf(ev));
    const mine = convoked.map(pid => {
      const r = responses.find(x => x.event_id === o.eventId && x.occurrence_date === o.date && x.person_id === pid);
      const verdict = verdictOf(pid, actx);
      const presence: Presence = r?.status === 'absent' ? 'absent' : r?.status === 'present' ? 'confirmed' : r?.status === 'uncertain' ? 'uncertain'
        : verdict?.state === 'off' ? 'unavailable' : 'expected';
      return { personId: pid, presence, verdict, reason: r?.reason ?? null };
    });
    let counts: AgendaItem['counts'] = null;
    let uncertain: AgendaItem['uncertain'] = [];
    const teamMembers = rosterOf.get(ev.team_id)?.filter(r => inSquad(r.squad_ids, match?.squad_id)).map(r => r.person_id);
    if (teamMembers) {
      const a = resolveAttendance(
        teamMembers,
        teamInv.filter(i => i.event_id === o.eventId && i.occurrence_date === o.date).map(i => i.person_id),
        teamResp.filter(r => r.event_id === o.eventId && r.occurrence_date === o.date).map(r => ({ personId: r.person_id, status: r.status, reason: r.reason })),
        actx,
      );
      counts = attendanceCounts(a);
      const roster = rosterAll.get(ev.team_id) ?? [];
      uncertain = a.uncertain.map(u => { const r = roster.find(x => x.person_id === u.personId); return { ...u, name: r ? displayName(r) : '—' }; });
    }
    return { occ: o, ev, team: teamOf(ctx, ev.team_id), match, label: matchTeamLabel(ctx, ev.team_id, match?.squad_id), mine, counts, uncertain };
  });
  return { today, tomorrow: addDays(today, 1), items, unav: avail.periods, rules: avail.rules, avail, leads };
}

/** « aujourd'hui », « demain », « dans 3 jours ». */
export function relativeDay(date: string, today: string): string {
  const d = Math.round((Date.parse(date) - Date.parse(today)) / 86400000);
  return d <= 0 ? "aujourd'hui" : d === 1 ? 'demain' : `dans ${d} jours`;
}
